/**
 * Connection Monitor Service
 *
 * Monitors internet connectivity and server reachability.
 * Provides real-time state updates for offline resilience features.
 *
 * --- 2026-05-24 — Bug fix: false negative on Android Capacitor WebView ---
 *
 * Old implementation trusted `navigator.onLine` exclusively. On Android
 * Chromium WebView (Capacitor 7 / Android 12), this signal gets stuck
 * `false` after suspend/resume even when ConnectivityManager reports
 * `INTERNET + VALIDATED + WIFI`. Result: tablets in Kiosk Mode with
 * perfectly working Wi-Fi were showing "Sem conexão com a internet" because
 * `navigator.onLine === false`.
 *
 * Fix: probes de rede REAIS, em vez de acreditar no navegador —
 *   - Server probe: HEAD <serverUrl>/<healthEndpoint>. Roda SEMPRE.
 *   - Internet probe: HEAD <internetProbeUrl> (no-cors), um host fora da nossa
 *     API. `no-cors` faz qualquer resposta resolvida (inclusive opaca) contar
 *     como sucesso — só importa alcançar a rede, não o corpo.
 *     **Roda SÓ quando o probe do servidor falha** (2026-10-23) — vide
 *     `performDualProbe`. Com o servidor respondendo, ele próprio já prova que
 *     há internet, e a matriz abaixo descartaria o resultado de qualquer jeito.
 *
 * State is derived from the matrix:
 *   internet ❌ + server ❌ → offline
 *   internet ✅ + server ❌ → server_unreachable
 *   server ✅               → online (o probe do servidor é autoritativo e
 *                                     dispensa o de internet; se ele responde,
 *                                     há internet por definição)
 *
 * `navigator.onLine` is still used as a CHEAP HINT (we listen to its events
 * to retry faster), but never as final truth.
 */

import { logSeMudou } from '~/utils/logSeMudou';
import {
  ConnectionState,
  ConnectionStateCallback,
  ConnectionMonitorConfig,
  IConnectionMonitorService,
} from './types';
import { recordSyncSample } from '~/services/time/serverClock';

const DEFAULT_PING_INTERVAL_ONLINE = 30 * 1000; // 30 seconds when online
const DEFAULT_PING_INTERVAL_OFFLINE = 5 * 1000; // 5 seconds when offline/degraded
const DEFAULT_PING_TIMEOUT = 8 * 1000; // 8s — Android WebView TLS handshake can be slow on flaky Wi-Fi
// 2026-05-24: switched from /api/health → /actuator/health. Spring Boot's
// /actuator/health é o endpoint anônimo padrão e retorna 200; /api/health
// no efflux-server retorna 401 (requer auth). Usando /api/health, a probe
// classificava o server como "reachable" só por sorte (qualquer status
// HTTP < 400 era aceito), e qualquer 401 caía como "server_unreachable".
const DEFAULT_HEALTH_ENDPOINT = '/actuator/health';
// Destino do probe de internet. Precisa ser um host FORA da nossa API — é a
// única forma de separar "a loja perdeu internet" de "nosso servidor caiu".
//
// 2026-10-23: passou a aceitar override por build (`VITE_INTERNET_PROBE_URL`).
// Numa instalação onde o cliente não queira tráfego para terceiro saindo da
// rede da loja, aponte para um host NOSSO que não seja a API — o CDN do app
// web, por exemplo. Mantém a distinção no caso comum ("a borda responde, a API
// não") e não acrescenta nenhuma entrada nova na allowlist do firewall.
// Com o probe sob demanda (vide performDualProbe) isso só é usado durante uma
// indisponibilidade real, então o default público custa ~nada.
const DEFAULT_INTERNET_PROBE_URL =
  import.meta.env.VITE_INTERNET_PROBE_URL ||
  'https://www.gstatic.com/generate_204';
// 2026-05-24: debounce de falhas consecutivas. 1 falha (cold-start do
// Railway, hiccup de rede móvel, throttle Wi-Fi de loja) NÃO deve mudar
// estado de UI/alertas pra "server_unreachable" — espera ver 2 falhas
// seguidas pra promover. Falha → recovery rápido (1 sucesso volta pra
// online imediato). Aplica só pra DEGRADAÇÃO de estado (online → algo
// pior); recovery não tem debounce.
const DEFAULT_FAILURE_DEBOUNCE = 2;

class ConnectionMonitorService implements IConnectionMonitorService {
  private config: Required<ConnectionMonitorConfig>;
  private callbacks: Set<ConnectionStateCallback> = new Set();
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private _isInternetReachable = true;
  private _isServerReachable = true;
  private _connectionState: ConnectionState = 'online';
  private isRunning = false;
  // 2026-05-24: counters pro debounce — promovemos a estado pior só após
  // N falhas consecutivas. Reset em qualquer probe bem-sucedido.
  private consecutiveInternetFailures = 0;
  private consecutiveServerFailures = 0;
  // Fase 1: assinantes do "conteúdo mudou" + linha de base de versão/ETag do
  // content-version (o probe do servidor descobre frescor de graça no ping).
  private readonly needsSyncCallbacks = new Set<
    (syncAfterSeconds?: number) => void
  >();
  private lastContentVersion: number | null = null;
  private lastContentEtag: string | null = null;

  constructor(config: ConnectionMonitorConfig) {
    this.config = {
      ...config,
      pingIntervalOnline:
        config.pingIntervalOnline ?? DEFAULT_PING_INTERVAL_ONLINE,
      pingIntervalOffline:
        config.pingIntervalOffline ?? DEFAULT_PING_INTERVAL_OFFLINE,
      pingTimeout: config.pingTimeout ?? DEFAULT_PING_TIMEOUT,
      healthEndpoint: config.healthEndpoint ?? DEFAULT_HEALTH_ENDPOINT,
      internetProbeUrl: config.internetProbeUrl ?? DEFAULT_INTERNET_PROBE_URL,
      failureDebounce: config.failureDebounce ?? DEFAULT_FAILURE_DEBOUNCE,
      // Fase 1: vazio = probe de servidor byte-idêntico ao anterior (HEAD no
      // healthEndpoint). Preenchido = probe passa a bater no content-version.
      contentVersionPath: config.contentVersionPath ?? '',
      tenantId: config.tenantId ?? '',
    };

    // Bind event handlers
    this.handleNavigatorOnline = this.handleNavigatorOnline.bind(this);
    this.handleNavigatorOffline = this.handleNavigatorOffline.bind(this);
  }

  // ===========================================================================
  // PUBLIC PROPERTIES
  // ===========================================================================

  /**
   * @deprecated DO NOT use as truth on Android. Prefer connectionState /
   * isInternetReachable / isServerReachable. Kept for legacy callers.
   */
  get isOnline(): boolean {
    if (typeof navigator !== 'undefined') {
      return navigator.onLine;
    }
    return true;
  }

  get isInternetReachable(): boolean {
    return this._isInternetReachable;
  }

  get isServerReachable(): boolean {
    return this._isServerReachable;
  }

  get connectionState(): ConnectionState {
    return this._connectionState;
  }

  get serverUrl(): string {
    return this.config.serverUrl;
  }

  // ===========================================================================
  // PUBLIC METHODS
  // ===========================================================================

  start(): void {
    if (this.isRunning) {
      console.log('[ConnectionMonitor] Already running');
      return;
    }

    // 2026-06-12 — Guard ambiente sem fetch (jsdom em testes, SSR).
    // Em produção (browser/WebView/Tizen/webOS) fetch sempre existe;
    // em jsdom NÃO existe e tentar probe gera erro spam + timeout.
    // Sem fetch o monitor é noop — estado fica nos defaults otimistas.
    if (typeof fetch === 'undefined') {
      console.log(
        '[ConnectionMonitor] fetch indisponível (ambiente de teste/SSR) — monitor inerte',
      );
      return;
    }

    console.log('[ConnectionMonitor] Starting connection monitoring');
    this.isRunning = true;

    // Listen for navigator events as HINTS (cheap signals to re-probe fast).
    // We do NOT mutate state from these — the probes do.
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleNavigatorOnline);
      window.addEventListener('offline', this.handleNavigatorOffline);
    }

    // Start periodic dual-probe (immediate + interval)
    this.startPing();
  }

  stop(): void {
    if (!this.isRunning) {
      return;
    }

    console.log('[ConnectionMonitor] Stopping connection monitoring');
    this.isRunning = false;

    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleNavigatorOnline);
      window.removeEventListener('offline', this.handleNavigatorOffline);
    }

    this.stopPing();
  }

  async checkNow(): Promise<boolean> {
    return this.performDualProbe();
  }

  onStateChange(callback: ConnectionStateCallback): () => void {
    this.callbacks.add(callback);
    // Immediately notify with current state
    callback(this._connectionState);
    return () => {
      this.callbacks.delete(callback);
    };
  }

  /**
   * Fase 1: assina o "content-version mudou". Diferente de onStateChange, NÃO
   * notifica de imediato — só há o que sincronizar quando uma mudança real é
   * detectada num probe.
   */
  onNeedsSync(callback: (syncAfterSeconds?: number) => void): () => void {
    this.needsSyncCallbacks.add(callback);
    return () => {
      this.needsSyncCallbacks.delete(callback);
    };
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  /**
   * navigator.onLine event handlers act as cheap HINTS — we re-probe
   * immediately when the OS reports a state change, but we never assume the
   * navigator is correct (Android WebView lies).
   */
  private handleNavigatorOnline(): void {
    console.log('[ConnectionMonitor] navigator hint: online → re-probing');
    this.performDualProbe();
  }

  private handleNavigatorOffline(): void {
    console.log('[ConnectionMonitor] navigator hint: offline → re-probing');
    // Don't blindly mark offline — Android WebView often reports offline
    // while Wi-Fi is actually OK. Re-probe to confirm.
    this.performDualProbe();
  }

  private startPing(): void {
    this.stopPing();

    const interval =
      this._connectionState === 'online'
        ? this.config.pingIntervalOnline
        : this.config.pingIntervalOffline;

    // Immediate check
    this.performDualProbe();

    // Schedule periodic checks
    this.pingInterval = setInterval(() => {
      this.performDualProbe();
    }, interval);

    console.log(`[ConnectionMonitor] Ping started (every ${interval / 1000}s)`);
  }

  private stopPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Roda o probe do servidor e, SÓ SE ELE FALHAR, o probe de internet.
   * Returns true if the SERVER is reachable (kept for backward compat with
   * checkNow's contract — callers like SyncService care about the server).
   *
   * 2026-05-24: Debounce — uma única falha consecutiva NÃO promove a estado
   * pior. Promove só após {@code failureDebounce} falhas seguidas. Recovery
   * (success after failure) é instantâneo — qualquer probe OK reseta counter
   * e volta pra 'online'. Logs incluem o counter pra diagnóstico em campo.
   *
   * 2026-10-23 (Renato): o probe de internet passou a ser SOB DEMANDA.
   * Antes os dois rodavam em paralelo a cada ciclo (30s online / 5s degradado),
   * em toda a frota. Só que a máquina de estado abaixo **descarta** `internetOk`
   * sempre que `serverOk` é true — o ramo `if (serverOk)` nem o lê. Ou seja: em
   * operação normal o resultado era calculado e jogado fora 2×/min por player.
   * Em 6000 telas isso eram ~17 milhões de requisições/dia a um terceiro,
   * saindo de dentro da rede de cada loja, para alimentar um rótulo de status.
   *
   * O probe de internet só informa algo quando o servidor falha — é ele que
   * separa "a loja perdeu internet" de "a internet está boa, nosso servidor
   * caiu". Quando o servidor RESPONDE, a pergunta já está respondida: o próprio
   * servidor é um host público na internet, então alcançá-lo PROVA que há
   * internet. Daí `internetOk = true` por dedução, sem request nenhum.
   */
  private async performDualProbe(): Promise<boolean> {
    const previousState = this._connectionState;

    // Fase 1: com content-version configurado, o probe do servidor também
    // descobre frescor de conteúdo (um request faz as duas coisas). Sem ele,
    // segue o probe de saúde HEAD de sempre.
    const serverProbe = this.config.contentVersionPath
      ? this.pingContentVersion()
      : this.pingServer();

    const serverOk = await serverProbe;

    // Sondamos já na PRIMEIRA falha do servidor (não só depois do debounce):
    // quando o debounce fechar, os contadores de internet precisam ter o mesmo
    // número de amostras de antes, senão a decisão offline × server_unreachable
    // escorrega um ciclo.
    const internetOk = serverOk ? true : await this.pingInternet();

    // Counters de falhas consecutivas — reset em sucesso, incrementa em falha
    if (internetOk) {
      this.consecutiveInternetFailures = 0;
    } else {
      this.consecutiveInternetFailures++;
    }

    if (serverOk) {
      this.consecutiveServerFailures = 0;
    } else {
      this.consecutiveServerFailures++;
    }

    this._isInternetReachable = internetOk;
    this._isServerReachable = serverOk;

    // Aplica debounce: falha só "conta" pra mudança de estado após N seguidas
    const debouncedInternetFail =
      this.consecutiveInternetFailures >= this.config.failureDebounce;
    const debouncedServerFail =
      this.consecutiveServerFailures >= this.config.failureDebounce;

    // RUÍDO (2026-10-15): a sonda roda sem parar e o resultado quase nunca muda.
      // Quem diagnostica procura a TRANSIÇÃO (ok→falha, falha→ok) — ela continua visível.
      logSeMudou(
        'conexao.sonda',
        `[ConnectionMonitor] internet=${internetOk}(${this.consecutiveInternetFailures}/${this.config.failureDebounce}) `
          + `server=${serverOk}(${this.consecutiveServerFailures}/${this.config.failureDebounce})`,
      );

    // Derive state from debounced probe matrix
    let newState: ConnectionState;
    if (serverOk) {
      // Server probe is the strongest positive signal — if it works, we're
      // online (even if Google probe was blocked by corporate firewall).
      newState = 'online';
    } else if (!debouncedServerFail) {
      // Server falhou MAS ainda não bateu o debounce — mantém estado anterior
      // pra não dar falso alarme em hiccup de 1 probe (cold start Railway,
      // throttle de Wi-Fi de loja, jitter de rede móvel).
      newState = previousState;
    } else if (internetOk || !debouncedInternetFail) {
      // Internet OK (ou ainda em debounce) + server falhou de fato →
      // server unreachable. DNS, firewall ou server outage.
      newState = 'server_unreachable';
    } else {
      // Ambos falharam past debounce → real offline.
      newState = 'offline';
    }

    this.updateState(newState);

    // Adjust ping interval if state changed
    if (previousState !== this._connectionState) {
      this.startPing();
    }

    return serverOk;
  }

  /**
   * Probe public internet via Google's generate_204 endpoint. Uses no-cors
   * mode so any resolved response (including opaque) counts as success —
   * we only care about TCP/TLS reachability, not response content.
   */
  private async pingInternet(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        this.config.pingTimeout,
      );

      // no-cors → any successful network round-trip is a "pass", even if
      // the response is opaque (response.ok will be false but no exception
      // is thrown). That's exactly what we want for an internet liveness check.
      await fetch(this.config.internetProbeUrl, {
        method: 'HEAD',
        mode: 'no-cors',
        signal: controller.signal,
        cache: 'no-store',
      });

      clearTimeout(timeoutId);
      return true;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('[ConnectionMonitor] Internet probe timed out');
      } else {
        console.log(
          '[ConnectionMonitor] Internet probe failed:',
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
      return false;
    }
  }

  private async pingServer(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        this.config.pingTimeout,
      );

      const url = `${this.config.serverUrl}${this.config.healthEndpoint}`;

      const response = await fetch(url, {
        method: 'HEAD',
        signal: controller.signal,
        cache: 'no-store',
      });

      clearTimeout(timeoutId);

      // 2026-05-24: ANY HTTP response = server reachable.
      // O ponto desta probe é: "TCP/TLS/DNS pro servidor funcionam?". Se a
      // gente recebe 401, 403, 404, 500 — não importa: o SERVER está vivo e
      // respondendo. Quem decide se a rota tem auth é o caller que usa a rota,
      // não o connectivity check. Antes aceitava só 2xx/3xx, o que classificava
      // erroneamente como "unreachable" um server que respondia 401 (caso real
      // do efflux-server em /api/health).
      console.log(
        `[ConnectionMonitor] Server probe ${url} → ${response.status}`,
      );
      return true;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log(
          `[ConnectionMonitor] Server ping timed out (>${this.config.pingTimeout}ms) for ${this.config.serverUrl}${this.config.healthEndpoint}`,
        );
      } else {
        console.log(
          `[ConnectionMonitor] Server ping failed for ${this.config.serverUrl}${this.config.healthEndpoint}:`,
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
      return false;
    }
  }

  /**
   * Fase 1: probe do servidor que também descobre frescor de conteúdo.
   *
   * Bate no content-version (GET, condicional via If-None-Match) — um request
   * faz as duas coisas: prova de vida (QUALQUER resposta HTTP = alcançável,
   * igual ao pingServer; só timeout/erro de rede = inalcançável) e frescor.
   * Espelha o ContentPollingService: compara `body.version` (robusto mesmo
   * quando o ETag não é legível cross-origin) e também honra o `needs_sync` do
   * servidor. A PRIMEIRA resposta só estabelece a linha de base — nunca dispara
   * sync (senão a loja inteira que liga junta sincronizaria de uma vez).
   */
  private async pingContentVersion(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        this.config.pingTimeout,
      );

      const headers: Record<string, string> = {};
      if (this.lastContentEtag) headers['If-None-Match'] = this.lastContentEtag;

      const sentAt = Date.now();
      const response = await fetch(this.contentVersionUrl(), {
        method: 'GET',
        headers,
        signal: controller.signal,
        cache: 'no-store',
      });
      clearTimeout(timeoutId);
      // Amostra de relógio por round-trip, se o servidor carimbar a hora (vide serverClock).
      recordSyncSample(
        response.headers?.get?.('X-Server-Time'),
        sentAt,
        Date.now(),
      );

      const tag = response.headers?.get?.('ETag');
      if (tag) this.lastContentEtag = tag;

      // 304 é o caso comum: alcançável e nada mudou.
      if (response.status === 304) {
        return true;
      }

      if (response.ok) {
        let body: {
          version?: number;
          sync_after_seconds?: number;
          needs_sync?: boolean;
        } = {};
        try {
          body = await response.json();
        } catch {
          // Corpo ilegível não pode derrubar a prova de vida.
        }

        const versionChanged =
          this.lastContentVersion !== null &&
          typeof body.version === 'number' &&
          body.version !== this.lastContentVersion;
        if (typeof body.version === 'number') {
          this.lastContentVersion = body.version;
        }

        // `needs_sync` do servidor (quando o If-None-Match fechou) OU mudança de
        // versão detectada aqui (fallback robusto). Ambos só valem após a linha
        // de base: `versionChanged` exige `lastContentVersion !== null`, e o
        // `needs_sync` do servidor já é false sem If-None-Match (1ª consulta).
        if (body.needs_sync === true || versionChanged) {
          console.log(
            '[ConnectionMonitor] content-version mudou → dispara sync',
          );
          this.notifyNeedsSync(body.sync_after_seconds);
        }
        return true;
      }

      // 4xx/5xx: o servidor respondeu, logo está vivo (igual pingServer). Não
      // mexe na linha de base de versão.
      // 🔴 Mas o corpo do erro (JSON/HTML) tem bytes de verdade e ninguém o lê:
      // largar a `Response` assim segura o data pipe do Chromium e reserva
      // ~2 MB de address space por requisição no renderer 32-bit, que nunca
      // voltam (causa-raiz do renderer-kill medida no Tab A7 — commit 683464d).
      // O 304 acima não precisa disto (resposta sem corpo, provado por A/B).
      await response.body?.cancel().catch(() => undefined);
      console.log(
        `[ConnectionMonitor] content-version probe → ${response.status}`,
      );
      return true;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('[ConnectionMonitor] content-version probe timed out');
      } else {
        console.log(
          '[ConnectionMonitor] content-version probe failed:',
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
      return false;
    }
  }

  private contentVersionUrl(): string {
    const base = `${this.config.serverUrl}${this.config.contentVersionPath}`;
    return this.config.tenantId
      ? `${base}?tenant=${encodeURIComponent(this.config.tenantId)}`
      : base;
  }

  private notifyNeedsSync(syncAfterSeconds?: number): void {
    this.needsSyncCallbacks.forEach((callback) => {
      try {
        callback(syncAfterSeconds);
      } catch (error) {
        console.error(
          '[ConnectionMonitor] Error in needs-sync callback:',
          error,
        );
      }
    });
  }

  private updateState(newState: ConnectionState): void {
    if (this._connectionState === newState) {
      return;
    }

    const previousState = this._connectionState;
    this._connectionState = newState;

    console.log(
      `[ConnectionMonitor] State changed: ${previousState} -> ${newState} ` +
        `(internet=${this._isInternetReachable}, server=${this._isServerReachable})`,
    );

    // Notify all callbacks
    this.callbacks.forEach((callback) => {
      try {
        callback(newState);
      } catch (error) {
        console.error(
          '[ConnectionMonitor] Error in state change callback:',
          error,
        );
      }
    });
  }

  // ===========================================================================
  // CLEANUP
  // ===========================================================================

  destroy(): void {
    this.stop();
    this.callbacks.clear();
  }
}

export default ConnectionMonitorService;
